import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, CircularProgress, InputAdornment, IconButton, Alert,
} from '@mui/material';
import {
  LockOutlined, MailOutline, Visibility, VisibilityOff, LoginRounded,
} from '@mui/icons-material';
import { colors, brand } from '../../theme/theme';
import { useAuthService } from '../../hooks/useAuthService';

/**
 * Admin login — **id (email) + password**. This is the piece missing from the
 * admin-prototype. By default it authenticates against built-in mock
 * credentials (see useAuthService); wire REACT_APP_USE_MOCK_AUTH=false to use a
 * real backend.
 */
const LoginScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, setLoginDetails, isAuthenticated } = useAuthService();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const from = (location.state as any)?.from || '/dashboard';

  useEffect(() => {
    if (isAuthenticated()) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  const valid = /\S+@\S+\.\S+/.test(email) && password.length >= 4;

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!valid || loading) return;
    setLoading(true);
    setError('');
    try {
      const { accessToken, refreshToken, user } = await login(email, password);
      setLoginDetails(accessToken, refreshToken, user);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, password, valid, loading, login, setLoginDetails, navigate, from]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Left — brand panel */}
      <Box
        sx={{
          flex: 1, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'space-between',
          p: 6, color: '#fff', position: 'relative', overflow: 'hidden',
          background: 'radial-gradient(circle at 20% 20%, #1D2A44 0%, #0B1220 60%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="img" src="/logo192.png" alt="BaseQueue" sx={{ width: 40, height: 40, borderRadius: '10px' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>BaseQueue</Typography>
            <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600 }}>Admin Console</Typography>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 38, lineHeight: 1.15, mb: 2 }}>
            Run the whole platform
            <Box component="span" sx={{ display: 'block', background: 'linear-gradient(90deg, #60A5FA, #34D399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              from one console.
            </Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 15, maxWidth: 460, lineHeight: 1.6 }}>
            Businesses, queues, consumers, categories, payments and reports — a single,
            read-first Super Admin & Provider control center.
          </Typography>
        </Box>

        <Typography sx={{ color: '#64748B', fontSize: 12.5 }}>© {new Date().getFullYear()} BaseQueue Technologies</Typography>
      </Box>

      {/* Right — login form */}
      <Box sx={{ width: { xs: '100%', md: 460 }, minWidth: { md: 460 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', px: { xs: 3, md: 6 }, py: 4, bgcolor: colors.backgroundPaper }}>
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 3, justifyContent: 'center' }}>
            <Box component="img" src="/logo192.png" alt="BaseQueue" sx={{ width: 44, height: 44, borderRadius: '10px' }} />
            <Typography sx={{ fontWeight: 800, fontSize: 20 }}>BaseQueue Admin</Typography>
          </Box>

          <Typography sx={{ fontWeight: 800, fontSize: 26, mb: 0.5 }}>Welcome back</Typography>
          <Typography sx={{ color: colors.textSecondary, fontSize: 14.5, mb: 4 }}>
            Sign in with your admin ID and password to continue.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{error}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.gray700, mb: 0.75 }}>Admin ID (Email)</Typography>
            <TextField
              fullWidth
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@basequeue.com"
              autoFocus
              disabled={loading}
              sx={{ mb: 2.5 }}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MailOutline sx={{ fontSize: 20, color: colors.textTertiary }} /></InputAdornment>) }}
            />

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.gray700, mb: 0.75 }}>Password</Typography>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              disabled={loading}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><LockOutlined sx={{ fontSize: 20, color: colors.textTertiary }} /></InputAdornment>),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" size="small">
                      {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={!valid || loading}
              endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginRounded />}
              sx={{
                height: 50, borderRadius: '12px', fontSize: 16, fontWeight: 700,
                background: valid ? brand.gradBrand : colors.gray200,
                color: valid ? '#fff' : colors.textTertiary,
                '&.Mui-disabled': { background: colors.gray200, color: colors.textTertiary },
              }}
            >
              Sign In
            </Button>
          </Box>

          {(process.env.REACT_APP_USE_MOCK_AUTH ?? 'true') !== 'false' && (
            <Box sx={{ mt: 3, p: 1.75, borderRadius: '10px', bgcolor: colors.gray50, border: `1px dashed ${colors.border}` }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, mb: 0.5 }}>Demo credentials</Typography>
              <Typography sx={{ fontSize: 12.5, color: colors.textSecondary, fontFamily: 'ui-monospace, monospace' }}>
                {process.env.REACT_APP_ADMIN_EMAIL || 'admin@basequeue.com'} / {process.env.REACT_APP_ADMIN_PASSWORD || 'admin123'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LoginScreen;
